import React from "react";
import { Trans } from "@coworker/locales";
import { ItemWithPieces } from "@coworker/apprestructured/src/shared/types/item";
import { ItemNoAndTypeWithOrderedDetails } from "@coworker/apprestructured/src/task-management/types/TaskDetailTypes";
import { Loading } from "../../../loading";
import FixaCheckbox from "@coworker/apprestructured/src/shared/wrappers/FixaCheckbox/FixaCheckbox";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import styles from "./EditArticlesOrderedDetailsContent.module.css";
import { ItemCardWithPieces } from "@coworker/apprestructured/src/shared/components/item-cards/ItemCardWithPieces/ItemCardWithPieces";
import { CheckboxFieldConfig } from "@coworker/apprestructured/src/task-management/types/OrderedDetailsMode";

interface EditArticlesOrderedDetailsContentProps {
  items: ItemWithPieces[];
  itemNosAndTypeWithOrderedDetails:
    ItemNoAndTypeWithOrderedDetails[] | null | Loading;
  fieldConfigs: CheckboxFieldConfig[];
  onCompleteChange: (
    itemNo: string,
    field: "pieces_ordered" | "ptags_ordered",
    completed: boolean
  ) => void;
}

type CheckboxRowProps = {
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  value: string;
  translationKey: string;
  count: number;
};

const CheckboxRow = ({
  id,
  checked,
  onChange,
  value,
  translationKey,
  count,
}: CheckboxRowProps) => (
  <div className={styles["checkbox-text-row"]}>
    <FixaCheckbox
      id={id}
      value={value}
      checked={checked}
      onChange={() => onChange(!checked)}
    />
    <FixaText
      tagName="p"
      textStyle="Body.M"
      className={checked ? styles["strikethrough"] : undefined}
    >
      <Trans values={{ count }}>{translationKey}</Trans>
    </FixaText>
  </div>
);

export const EditArticlesOrderedDetailsContent = ({
  items,
  itemNosAndTypeWithOrderedDetails,
  fieldConfigs,
  onCompleteChange,
}: EditArticlesOrderedDetailsContentProps) => {
  const orderedDetailsByItemNo = new Map(
    Array.isArray(itemNosAndTypeWithOrderedDetails)
      ? itemNosAndTypeWithOrderedDetails.map(
          ({ no, pieces_ordered, ptags_ordered }) => [
            no,
            { pieces_ordered, ptags_ordered },
          ]
        )
      : []
  );

  return (
    <>
      {items.map((item) => (
        <React.Fragment key={item.no}>
          <ItemCardWithPieces item={item} />
          <div className={styles["checkbox-rows"]}>
            {fieldConfigs.map((fieldConfig) => (
              <CheckboxRow
                key={fieldConfig.id}
                id={`item-ordered-details-checkbox-${fieldConfig.id}-${item.no}`}
                value={item.no}
                checked={
                  !!orderedDetailsByItemNo.get(item.no)?.[fieldConfig.fieldPath]
                    ?.completed
                }
                onChange={(checked) =>
                  onCompleteChange(item.no, fieldConfig.fieldPath, checked)
                }
                translationKey={fieldConfig.translationKey}
                count={
                  orderedDetailsByItemNo.get(item.no)?.[fieldConfig.fieldPath]
                    ?.quantity ?? 0
                }
              />
            ))}
          </div>
        </React.Fragment>
      ))}
    </>
  );
};
